import React, { type ReactElement, type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSlug from 'rehype-slug'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import { Link as InertiaLink } from '@inertiajs/react'
import type { SxProps, Theme } from '@mui/material/styles'
import type { TypographyProps } from '@mui/material/Typography'

import ViewKanbanIcon from '@mui/icons-material/ViewKanban'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import DescriptionIcon from '@mui/icons-material/Description'
import FolderIcon from '@mui/icons-material/Folder'
import PersonIcon from '@mui/icons-material/Person'
import TerminalIcon from '@mui/icons-material/Terminal'
import LinkIcon from '@mui/icons-material/Link'

type InternalLinkInfo = { entityType: string; icon: ReactElement }

const iconSx = { fontSize: 16 }

function getInternalLinkInfo(href: string): InternalLinkInfo | null {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  if (origin && href.startsWith(origin)) {
    href = href.slice(origin.length) || '/'
  }

  if (!href.startsWith('/')) return null

  if (/^\/tasks\/boards\/[^/]+\/tasks\/\d+/.test(href))
    return { entityType: 'task', icon: <TaskAltIcon sx={iconSx} /> }
  if (/^\/tasks\/boards\/[^/]+\/?$/.test(href))
    return { entityType: 'board', icon: <ViewKanbanIcon sx={iconSx} /> }
  if (/^\/notebooks\/notebooks\/[^/]+\/notes\/\d+/.test(href))
    return { entityType: 'note', icon: <DescriptionIcon sx={iconSx} /> }
  if (/^\/notebooks\/notebooks\/[^/]+\/folders\/\d+/.test(href))
    return { entityType: 'folder', icon: <FolderIcon sx={iconSx} /> }
  if (/^\/notebooks\/notebooks\/[^/]+\/?$/.test(href))
    return { entityType: 'notebook', icon: <MenuBookIcon sx={iconSx} /> }
  if (/^\/admin\/agents\/[^/]+/.test(href))
    return { entityType: 'agent', icon: <PersonIcon sx={iconSx} /> }
  if (/^\/agents\/session_logs\/\d+/.test(href))
    return { entityType: 'session_log', icon: <TerminalIcon sx={iconSx} /> }

  return { entityType: 'internal', icon: <LinkIcon sx={iconSx} /> }
}

function extractText(node: ReactNode): string {
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (node == null || typeof node === 'boolean') return ''
  if (Array.isArray(node)) return node.map(extractText).join('')
  if (typeof node === 'object' && 'props' in node) {
    const el = node as ReactElement & { props: { children?: ReactNode } }
    return extractText(el.props.children)
  }
  return ''
}

interface Props {
  children: string
  variant?: TypographyProps['variant']
  sx?: SxProps<Theme>
}

export function Markdown({ children, variant = 'body1', sx }: Props) {
  const isSmall = variant === 'body2' || variant === 'caption'
  const codeSize = isSmall ? '0.8rem' : '0.85rem'

  return (
    <Box
      sx={{
        ...sx as object,
        overflowX: 'auto',
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug]}
        components={{
          p: ({ children }) => (
            <Typography variant={variant} sx={{ my: 0.5 }}>
              {children}
            </Typography>
          ),
          a: ({ href, children }) => {
            if (!href) return <Link href="#">{children}</Link>

            if (href.startsWith('#')) {
              return <Link href={href}>{children}</Link>
            }

            const linkInfo = getInternalLinkInfo(href)
            if (!linkInfo) {
              return (
                <Link href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </Link>
              )
            }

            return (
              <Chip
                component={InertiaLink}
                href={href}
                icon={linkInfo.icon}
                label={extractText(children) || href}
                size="small"
                variant="outlined"
                clickable
                sx={{ height: 22, fontSize: '0.75rem', verticalAlign: 'middle', mx: 0.25 }}
              />
            )
          },
          strong: ({ children }) => <strong>{children}</strong>,
          em: ({ children }) => <em>{children}</em>,
          del: ({ children }) => <del>{children}</del>,
          h1: ({ children }) => <Typography variant="h6" sx={{ my: 1 }}>{children}</Typography>,
          h2: ({ children }) => <Typography variant="h6" sx={{ my: 1 }}>{children}</Typography>,
          h3: ({ children }) => <Typography variant="subtitle1" fontWeight={600} sx={{ my: 0.5 }}>{children}</Typography>,
          h4: ({ children }) => <Typography variant="subtitle2" fontWeight={600} sx={{ my: 0.5 }}>{children}</Typography>,
          h5: ({ children }) => <Typography variant="subtitle2" fontWeight={600} sx={{ my: 0.5 }}>{children}</Typography>,
          h6: ({ children }) => <Typography variant="subtitle2" fontWeight={600} sx={{ my: 0.5 }}>{children}</Typography>,
          ul: ({ children }) => (
            <Box component="ul" sx={{ my: 0.5, pl: 2.5, fontSize: isSmall ? '0.875rem' : '1rem' }}>
              {children}
            </Box>
          ),
          ol: ({ children }) => (
            <Box component="ol" sx={{ my: 0.5, pl: 2.5, fontSize: isSmall ? '0.875rem' : '1rem' }}>
              {children}
            </Box>
          ),
          li: ({ children }) => (
            <Box component="li" sx={{ my: 0.25 }}>
              {children}
            </Box>
          ),
          blockquote: ({ children }) => (
            <Box
              component="blockquote"
              sx={{
                my: 0.5,
                mx: 0,
                pl: 1.5,
                borderLeft: '3px solid',
                borderColor: 'divider',
                color: 'text.secondary',
              }}
            >
              {children}
            </Box>
          ),
          code: ({ children }) => (
            <Box
              component="code"
              sx={{
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                bgcolor: 'grey.100',
                fontSize: codeSize,
                fontFamily: 'monospace',
              }}
            >
              {children}
            </Box>
          ),
          pre: ({ children }) => (
            <Box
              component="pre"
              sx={{
                my: 1,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'grey.100',
                overflow: 'auto',
                fontSize: codeSize,
                fontFamily: 'monospace',
                '& code': {
                  p: 0,
                  borderRadius: 0,
                  bgcolor: 'transparent',
                  fontSize: 'inherit',
                },
              }}
            >
              {children}
            </Box>
          ),
          hr: () => <Box component="hr" sx={{ my: 1, border: 'none', borderTop: '1px solid', borderColor: 'divider' }} />,
          table: ({ children }) => (
            <Box
              component="table"
              sx={{
                my: 1,
                borderCollapse: 'collapse',
                fontSize: isSmall ? '0.875rem' : '1rem',
                '& th, & td': {
                  border: '1px solid',
                  borderColor: 'divider',
                  px: 1,
                  py: 0.5,
                },
                '& th': {
                  bgcolor: 'grey.50',
                  fontWeight: 600,
                },
              }}
            >
              {children}
            </Box>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </Box>
  )
}
